import { cva } from '@styled-system/css';
import { ExternalLink, ImageOff } from 'lucide-react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { AnalysisPlatform } from '@/shared/api/types/generated';
import { isVestiaireAnalysis } from '@/shared/utils/analysis/platform';
import { type AnalysisIntentSource, getAnalysisIntent } from '@/shared/utils/analysis/searchIntent';

// ============================================================================
// Chips d'une analyse (statut + intention + lien Vinted) — affichées dans le
// header de page. Partagées entre analyse produit et vendeur.
// ============================================================================

const chip = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2',
    paddingY: '1',
    paddingX: '3',
    borderRadius: 'full',
    fontSize: 'caption',
    fontWeight: 'medium',
    borderWidth: '1px',
    borderStyle: 'solid',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  variants: {
    tone: {
      discovery: { bg: 'info.soft', color: 'info.default', borderColor: 'info.border' },
      targeted: { bg: 'surface.sunken', color: 'text.tertiary', borderColor: 'border.default' },
      degraded: { bg: 'warning.soft', color: 'warning.default', borderColor: 'warning.border' },
    },
  },
  defaultVariants: { tone: 'targeted' },
});

const externalLinkStyle = cva({
  base: {
    display: 'inline-flex',
    color: 'text.tertiary',
    flexShrink: 0,
    _hover: { color: 'brand.primary' },
  },
});

export interface AnalysisChipsProps {
  pageUrl?: string | null;
  platform: AnalysisPlatform;
}

/** Lien externe vers la recherche source (Vinted ou Vestiaire Collective selon la plateforme). */
export const AnalysisVintedLink: FC<AnalysisChipsProps> = ({ pageUrl, platform }) => {
  const { t } = useTranslation();
  if (!pageUrl) return null;

  const label = t(platform === 'vestiaire' ? 'analysisDetail.openOnVestiaire' : 'analysisDetail.openOnVinted');

  return (
    <a
      href={pageUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={externalLinkStyle()}
      aria-label={label}
      title={label}
    >
      <ExternalLink size={15} strokeWidth={1.75} />
    </a>
  );
};

/**
 * Chip « recherche visuelle uniquement » — relance produit dont le signal
 * texte était indisponible à la création (similarityDegraded) : le résultat
 * n'est pas strictement équivalent à une recherche fusionnée normale.
 */
export const AnalysisSimilarityDegradedChip: FC<{ degraded?: boolean }> = ({ degraded }) => {
  const { t } = useTranslation();
  if (!degraded) return null;

  return (
    <span className={chip({ tone: 'degraded' })} title={t('analysisDetail.similarityDegradedHint')}>
      <ImageOff size={11} strokeWidth={1.75} />
      {t('analysisDetail.similarityDegraded')}
    </span>
  );
};

/**
 * Chip d'intention — « Niche large » / « Niche ciblée ». Un zoom (recherche
 * fille) ou une recherche par image est toujours ciblée.
 */
export const AnalysisIntentChip: FC<{ analysis: AnalysisIntentSource | null | undefined }> = ({ analysis }) => {
  const { t } = useTranslation();
  // VC : `pageUrl` seul ne dit rien de l'intention (mots-clés au mieux) — le
  // filtre vient de `vestiaireFilter`, cf isVestiaireAnalysis.
  if (!analysis?.pageUrl && !isVestiaireAnalysis(analysis)) return null;
  const intent = getAnalysisIntent(analysis);

  return (
    <span className={chip({ tone: intent })}>
      {intent === 'discovery' ? t('productSearch.intentDiscovery') : t('productSearch.intentTargeted')}
    </span>
  );
};
